import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { EditPropsStore } from '@blocksuite/affine/shared/services';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { AwarenessStore, RawAwarenessState } from '@blocksuite/store';
import type { LiveData } from '@toeverything/infra';

export function findPersonCursor(
  states: Iterable<RawAwarenessState>,
  docId: string,
  userId: string
) {
  let latest: { x: number; y: number; updatedAt: number } | null = null;
  for (const state of states) {
    if (state.user?.id !== userId) continue;
    const saved = state.lastCanvasCursor;
    if (
      saved?.scope.startsWith(`${docId}:`) &&
      Number.isFinite(saved.x) &&
      Number.isFinite(saved.y) &&
      Number.isFinite(saved.updatedAt) &&
      (!latest || saved.updatedAt > latest.updatedAt)
    )
      latest = saved;
    if (latest) continue;
    for (const [scope, selections] of Object.entries(state.selectionV2 ?? {})) {
      if (!scope.startsWith(`${docId}:`)) continue;
      for (const cursor of selections) {
        if (
          cursor.type === 'cursor' &&
          typeof cursor.x === 'number' &&
          Number.isFinite(cursor.x) &&
          typeof cursor.y === 'number' &&
          Number.isFinite(cursor.y)
        )
          latest = { x: cursor.x, y: cursor.y, updatedAt: 0 };
      }
    }
  }
  return latest;
}

/** Two frames let the scheduled raster and DOM viewport updates paint while covered. */
function waitForViewportPaint(view: Window, signal: AbortSignal) {
  return new Promise<void>(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }
    let frame: number;
    const complete = () => {
      view.cancelAnimationFrame(frame);
      signal.removeEventListener('abort', complete);
      resolve();
    };
    signal.addEventListener('abort', complete, { once: true });
    frame = view.requestAnimationFrame(() => {
      frame = view.requestAnimationFrame(complete);
    });
  });
}

/** Called only by an actual Workbench document navigation, never a search preview. */
export function focusPersonOnOpen(
  container: AffineEditorContainer,
  awareness: AwarenessStore,
  docId: string,
  userId: string,
  enabled$: LiveData<boolean | null>,
  verifyPolicy?: (signal: AbortSignal) => Promise<boolean>
): (() => void) & { ready: Promise<void> } {
  let disposed = false;
  const controller = new AbortController();
  let resolveReady!: () => void;
  const ready = new Promise<void>(resolve => {
    resolveReady = resolve;
  });
  let unsubscribe: (() => void) | undefined;
  const finish = () => {
    if (disposed) return;
    disposed = true;
    controller.abort();
    clearTimeout(timeout);
    unsubscribe?.();
    resolveReady();
  };
  // Bound policy validation; never move the camera after the opening barrier.
  const timeout = setTimeout(finish, 8_000);
  const subscription = enabled$.subscribe(value => {
    if (value === false) queueMicrotask(finish);
  });
  unsubscribe = () => subscription.unsubscribe();
  void (async () => {
    await container.updateComplete;
    await container.host?.updateComplete;
    if (
      disposed ||
      enabled$.value !== true ||
      (verifyPolicy && !(await verifyPolicy(controller.signal)))
    )
      return;
    if (disposed || enabled$.value !== true) return;
    const cursor = findPersonCursor(
      awareness.getStates().values(),
      docId,
      userId
    );
    const std = container.host?.std;
    const gfx = std?.get(GfxControllerIdentifier);
    if (!cursor || !std || !gfx) return;
    // Reuse the canonical per-document viewport. Missing presence leaves the
    // already restored position and zoom intact, with no later cursor jump.
    try {
      std.get(EditPropsStore).setStorage('viewport', {
        centerX: cursor.x,
        centerY: cursor.y,
        zoom: 1,
      });
    } catch {
      /* Storage may be unavailable; the opening viewport still applies. */
    }
    gfx.viewport.setViewport(1, [cursor.x, cursor.y], false);
    await container.host?.updateComplete;
    const view = container.origin?.ownerDocument.defaultView;
    if (!disposed && view) await waitForViewportPaint(view, controller.signal);
  })()
    .catch(() => {})
    .finally(finish);
  return Object.assign(finish, { ready });
}
